import React from 'react';
import { ChevronDown, ChevronUp, Info, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { NumberField } from '@/components/ui/number-field';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BUSINESS_METRICS, PARTICIPATION_VS_ATTRIBUTION_NOTE, WHOOP_ACCURACY_DISCLAIMER } from '../data/model';
import type { BusinessMetricId, BusinessMetricInputs, RoiCompanyInputs } from '../data/model';

interface Props {
  company: RoiCompanyInputs;
  onChange: (next: RoiCompanyInputs) => void;
  onReset: () => void;
  businessMetricInputs: BusinessMetricInputs;
  onBusinessMetricInputChange: (id: BusinessMetricId, field: 'then' | 'now', value: number) => void;
  /** True in sales/demo mode (PEAK-271): figures are fixed, so inputs are read-only. */
  disabled?: boolean;
}

type NumericKey = Exclude<keyof RoiCompanyInputs, 'participationRate' | 'attributionRate'>;

interface FieldDef {
  key: NumericKey;
  label: string;
  prefix?: string;
  step?: number;
  // Unlike the lever conversions in model.ts, none of these defaults trace to
  // a specific published study — they're generic starting assumptions, so the
  // tooltip says that plainly rather than inventing a citation.
  tooltip: string;
}

const FIELDS: FieldDef[] = [
  {
    key: 'employees',
    label: 'Employees in the programme',
    tooltip: 'Default headcount assumption. Replace with the actual number of employees enrolled.',
  },
  {
    key: 'programmeCostPerEmployee',
    label: 'Programme cost / employee / yr',
    prefix: '£',
    tooltip: 'Default Peak4 programme cost assumption. Confirm against your organisation’s actual contract rate.',
  },
];

// Not shown on the main card: these don't describe the workforce directly,
// they're bridging assumptions three of the biometric levers (Sleep
// Duration, Resting Heart Rate, HRV — see data/model.ts) need to turn a
// measured change into a £ value. Kept editable, just out of the way.
const ADVANCED_FIELDS: FieldDef[] = [
  {
    key: 'averageSalary',
    label: 'Fully loaded average staff cost / yr',
    prefix: '£',
    tooltip:
      'Feeds the Sleep Duration lever’s £ conversion. A general starting assumption — replace with your organisation’s actual fully loaded average salary.',
  },
  {
    key: 'costPerSickDay',
    label: 'Cost per sickness day',
    prefix: '£',
    tooltip:
      'Feeds the Resting Heart Rate and HRV levers’ £ conversion. A general starting assumption for the cost of one lost working day — replace with your own figure if known.',
  },
  {
    key: 'absenceDaysPerEmployee',
    label: 'Average absence days / employee / yr',
    step: 0.1,
    tooltip:
      'Feeds the Resting Heart Rate and HRV levers’ £ conversion. A general starting assumption — replace with your organisation’s actual average sickness-absence days.',
  },
];

const FieldGrid: React.FC<{
  fields: FieldDef[];
  company: RoiCompanyInputs;
  setNumeric: (key: NumericKey, value: number) => void;
  disabled?: boolean;
}> = ({ fields, company, setNumeric, disabled = false }) => (
  <TooltipProvider delayDuration={200}>
    <div className='grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
      {fields.map((f) => (
        <div key={f.key} className='flex flex-col gap-1.5'>
          <div className='flex items-center gap-1'>
            <Label htmlFor={f.key}>{f.label}</Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type='button'
                  className='text-muted-foreground hover:text-foreground'
                  aria-label={`About ${f.label}`}
                >
                  <Info className='size-3' />
                </button>
              </TooltipTrigger>
              <TooltipContent>{f.tooltip}</TooltipContent>
            </Tooltip>
          </div>
          <NumberField
            id={f.key}
            value={company[f.key]}
            onChange={(v) => setNumeric(f.key, v)}
            min={0}
            step={f.step ?? 1}
            prefix={f.prefix}
            disabled={disabled}
          />
        </div>
      ))}
    </div>
  </TooltipProvider>
);

export const AssumptionsSection: React.FC<Props> = ({
  company,
  onChange,
  onReset,
  businessMetricInputs,
  onBusinessMetricInputChange,
  disabled = false,
}) => {
  const [advancedOpen, setAdvancedOpen] = React.useState(false);

  const setNumeric = (key: NumericKey, value: number) => {
    onChange({ ...company, [key]: Number.isFinite(value) && value >= 0 ? value : 0 });
  };

  const setRate = (key: 'participationRate' | 'attributionRate', pct: number) => {
    onChange({ ...company, [key]: pct / 100 });
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-base font-semibold'>Your company numbers</h2>
          <p className='text-sm text-muted-foreground'>
            Tune to your organisation. Participation rate should reflect the live figure from the Management Hub.
          </p>
        </div>
        <Button variant='outline' size='sm' onClick={onReset} disabled={disabled}>
          <RotateCcw />
          Reset defaults
        </Button>
      </div>

      {disabled && (
        <Alert>
          <AlertDescription>
            Sales demo mode is on — these figures are fixed for the demo and can't be edited. Turn it off to use your
            own numbers.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Workforce & cost inputs</CardTitle>
        </CardHeader>
        <CardContent className='flex flex-col gap-4'>
          <FieldGrid fields={FIELDS} company={company} setNumeric={setNumeric} disabled={disabled} />

          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger asChild>
              <Button variant='ghost' size='sm' className='-ml-2 h-7 w-fit px-2 text-xs'>
                {advancedOpen ? <ChevronUp /> : <ChevronDown />}
                {advancedOpen ? 'Hide advanced cost assumptions' : 'Show advanced cost assumptions'}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className='pt-3'>
              <p className='mb-3 text-xs text-muted-foreground'>
                These feed the Sleep Duration, Resting Heart Rate, and HRV levers' £ conversions specifically — they
                rarely need changing.
              </p>
              <FieldGrid fields={ADVANCED_FIELDS} company={company} setNumeric={setNumeric} disabled={disabled} />
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Business metrics (confirmed savings)</CardTitle>
          <CardDescription>
            Read these straight off your own HR/payroll records as a total cost, then vs now — no formula, no research
            citation needed. These count as confirmed savings, separate from the biometric-estimated savings on the
            Projected ROI tab.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
            {BUSINESS_METRICS.map((metric) => {
              const { then, now } = businessMetricInputs[metric.id];
              return (
                <div key={metric.id} className='flex flex-col gap-2 rounded-md border border-border p-3'>
                  <div>
                    <Label>{metric.title}</Label>
                    <p className='text-xs text-muted-foreground'>{metric.subtitle}</p>
                  </div>
                  <div className='grid grid-cols-2 gap-2'>
                    <div className='flex flex-col gap-1'>
                      <label className='text-xs font-medium text-muted-foreground'>Then</label>
                      <NumberField
                        value={then}
                        onChange={(v) => onBusinessMetricInputChange(metric.id, 'then', v)}
                        min={0}
                        prefix='£'
                        disabled={disabled}
                      />
                    </div>
                    <div className='flex flex-col gap-1'>
                      <label className='text-xs font-medium text-muted-foreground'>Now</label>
                      <NumberField
                        value={now}
                        onChange={(v) => onBusinessMetricInputChange(metric.id, 'now', v)}
                        min={0}
                        prefix='£'
                        disabled={disabled}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* The two rates are a matched pair and each holds one short field, so
          they sit side by side rather than as two near-empty full-width rows. */}
      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
        <Card className='flex flex-col'>
          <CardHeader>
            <CardTitle>Participation rate</CardTitle>
            <CardDescription>
              Share of employees actively engaging with the programme. Pull this from the Management Hub's participation
              figure rather than estimating it.
            </CardDescription>
          </CardHeader>
          <CardContent className='mt-auto'>
            <div className='w-40'>
              <NumberField
                value={Math.round(company.participationRate * 100)}
                onChange={(v) => setRate('participationRate', v)}
                min={0}
                max={100}
                suffix='%'
                aria-label='Participation rate'
                disabled={disabled}
              />
            </div>
          </CardContent>
        </Card>

        <Card className='flex flex-col'>
          <CardHeader>
            <CardTitle>Attribution rate</CardTitle>
            <CardDescription>
              Share of any improvement you credit to the programme rather than other causes. A judgement call the
              company makes, not a published figure.
            </CardDescription>
          </CardHeader>
          <CardContent className='mt-auto'>
            <div className='w-40'>
              <NumberField
                value={Math.round(company.attributionRate * 100)}
                onChange={(v) => setRate('attributionRate', v)}
                min={0}
                max={100}
                suffix='%'
                aria-label='Attribution rate'
                disabled={disabled}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Alert>
        <AlertDescription>{PARTICIPATION_VS_ATTRIBUTION_NOTE}</AlertDescription>
      </Alert>

      <Alert>
        <AlertDescription className='text-xs'>{WHOOP_ACCURACY_DISCLAIMER}</AlertDescription>
      </Alert>
    </div>
  );
};
